import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BankAccount } from '../../bank-account/bank-account.model';
import { BankAccountService } from '../../bank-account/bank-account.service';
import { BankReconciliationService } from '../bank-reconciliation.service';
import { BankStatement, BankStatementStatus } from '../models/bank-reconciliation.model';

@Component({
    selector: 'app-bank-statements',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, ButtonModule, SelectModule, TableModule, TagModule,
        PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <app-page-shell>
            <app-page-hero eyebrow="Finance Banking" title="Bank Reconciliation" subtitle="Import, validate, and review bank statements before reconciliation.">
                <div actions>
                    <button pButton type="button" icon="pi pi-upload" label="Import Statement" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/statements/import'])"></button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <app-page-data-panel title="Bank Statements" [resultText]="statements.length + ' statements'">
                <div actions class="toolbar">
                    <p-select [options]="bankAccounts" [(ngModel)]="selectedBankAccountId" optionLabel="accountName" optionValue="id" placeholder="All bank accounts" [showClear]="true" (onChange)="loadStatements()"></p-select>
                    <p-select [options]="statusOptions" [(ngModel)]="selectedStatus" placeholder="All statuses" [showClear]="true" (onChange)="loadStatements()"></p-select>
                </div>
                <p-table [value]="statements" [loading]="loading" styleClass="p-datatable-sm" [paginator]="true" [rows]="15">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Statement #</th>
                            <th>Bank Account</th>
                            <th>Period</th>
                            <th>Opening</th>
                            <th>Closing</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-statement>
                        <tr>
                            <td>{{ statement.statementNumber || ('STMT-' + statement.statementId) }}</td>
                            <td>{{ accountName(statement.bankAccountId) }}</td>
                            <td>{{ statement.periodStartDate || '-' }} to {{ statement.periodEndDate || '-' }}</td>
                            <td>{{ statement.openingBalance | number:'1.2-2' }} {{ statement.currencyCode }}</td>
                            <td>{{ statement.closingBalance | number:'1.2-2' }} {{ statement.currencyCode }}</td>
                            <td><p-tag [value]="statement.status" [severity]="severity(statement.status)"></p-tag></td>
                            <td class="actions"><button pButton type="button" icon="pi pi-eye" label="View" [text]="true" (click)="viewStatement(statement)"></button></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="7">No bank statements imported yet.</td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .toolbar { display: flex; flex-wrap: wrap; gap: .5rem; }
        .toolbar p-select { min-width: 220px; }
        .actions { text-align: right; }
    `]
})
export class BankStatementsComponent implements OnInit {
    statements: BankStatement[] = [];
    bankAccounts: BankAccount[] = [];
    selectedBankAccountId?: number;
    selectedStatus?: BankStatementStatus | '';
    loading = false;
    metrics: PageMetric[] = [];
    readonly statusOptions = ['IMPORTED', 'IN_RECONCILIATION', 'SUBMITTED', 'APPROVED', 'CLOSED'].map(value => ({ label: value, value }));

    constructor(
        private bankReconciliationService: BankReconciliationService,
        private bankAccountService: BankAccountService,
        private sessionStorageService: SessionStorageService,
        public router: Router
    ) {}

    ngOnInit(): void {
        this.loadBankAccounts();
        this.loadStatements();
    }

    loadBankAccounts(): void {
        const companyId = this.companyId();
        this.bankAccountService.getBankAccounts('Company', companyId, '', 0, 100).subscribe(result => this.bankAccounts = result.items ?? []);
    }

    loadStatements(): void {
        this.loading = true;
        this.bankReconciliationService.getStatements(this.companyId(), this.selectedBankAccountId, this.selectedStatus || undefined).subscribe({
            next: rows => {
                this.statements = rows ?? [];
                this.metrics = [
                    { label: 'Open statements', value: this.statements.filter(item => item.status !== 'CLOSED').length, tone: 'info' },
                    { label: 'Closed', value: this.statements.filter(item => item.status === 'CLOSED').length, tone: 'success' },
                    { label: 'Imported', value: this.statements.filter(item => item.status === 'IMPORTED').length, tone: 'warn' },
                    { label: 'Bank accounts', value: this.bankAccounts.length || '-', tone: 'info' }
                ];
                this.loading = false;
            },
            error: () => this.loading = false
        });
    }

    accountName(bankAccountId: number): string {
        return this.bankAccounts.find(account => account.id === bankAccountId)?.accountName ?? `Account ${bankAccountId}`;
    }

    viewStatement(statement: BankStatement): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/statements', statement.statementId]);
    }

    severity(status: BankStatementStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'CLOSED' || status === 'APPROVED') return 'success';
        if (status === 'IMPORTED') return 'info';
        if (status === 'CANCELLED') return 'danger';
        return 'warn';
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }
}
